/**
 * DANAmark Solidaritet — /join public page.
 *
 * Renders sections from `/api/membership` (or `/api/admin/membership/preview`
 * when ?preview=1 and the caller is admin). When state=draft the public
 * endpoint returns an empty section list — we render a 404 in that case so
 * the URL is not indexable until an admin flips state=live.
 *
 * Payment identifier & price are pulled from `settings` — never hard-coded
 * inside a section's data. Sections referencing payment (`payment` template
 * and `final_cta`) read from the same `settings` prop.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import resolveImageUrl from "@/lib/imageUrl";
import { usePageSEO } from "@/lib/seo";
import { RichText } from "@/components/RichText";

// Pick a localised string from an { en, da, es } bag with EN fallback so
// the page never renders blank strings while admins translate.
const pickLang = (obj, lang) => (obj && (obj[lang] || obj.en || obj.da || obj.es || "")) || "";
const nl2br = (s) => (s || "").split("\n").map((line, i) => (
  <React.Fragment key={i}>{line}{i < s.split("\n").length - 1 && <br />}</React.Fragment>
));

export default function Join() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageSEO(
    { en: "Become a member — DANAmark",
      da: "Bliv medlem — DANAmark",
      es: "Hazte miembro — DANAmark" },
    { en: "Join DANAmark — 200 DKK per year via MobilePay. Support the volunteer-led community between Denmark and Spain.",
      da: "Bliv medlem af DANAmark — 200 DKK om året via MobilePay.",
      es: "Hazte miembro de DANAmark — 200 DKK al año vía MobilePay." }
  );

  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).get("preview") === "1";
    const load = async () => {
      if (preview) {
        try {
          const r = await api.get("/admin/membership/preview", { withCredentials: true });
          setData(r.data);
          return;
        } catch {
          // Not admin — fall through and try public.
        }
      }
      try {
        const r = await api.get("/membership");
        // While state=draft the public endpoint returns no sections. Treat
        // that as a 404 so anonymous visitors never see a blank page.
        if ((r.data.sections || []).length === 0) {
          setNotFound(true);
          return;
        }
        setData(r.data);
      } catch {
        setNotFound(true);
      }
    };
    load();
  }, []);

  const scrollToPayment = () => {
    const el = document.getElementById("payment");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (notFound) {
    return (
      <div className="container-x max-editorial py-32 text-center">
        <div className="label-mono text-terracotta text-[10px]">404</div>
        <h1 className="font-editorial text-4xl md:text-5xl mt-3 tracking-tighter">Page not found.</h1>
        <p className="mt-4 text-ink/60">This page isn't available yet.</p>
        <Link to="/" className="mt-8 inline-block underline text-ink hover:text-terracotta">← Back to home</Link>
      </div>
    );
  }
  if (!data) return <div className="py-32 text-center text-ink/40">Loading…</div>;

  const { sections, settings, state } = data;

  return (
    <div className="bg-base text-ink pb-32" data-testid="join-page">
      {state === "draft" && (
        <div className="bg-amber-100 text-amber-900 text-[11px] label-mono tracking-widest text-center py-2 border-b border-amber-200">
          ADMIN PREVIEW · MEMBERSHIP PAGE IS NOT PUBLIC YET
        </div>
      )}
      {sections.map((s) => (
        <SectionRenderer key={s.id} section={s} lang={lang} settings={settings} onScrollPayment={scrollToPayment} />
      ))}
      <StickyMobileCta settings={settings} lang={lang} onClick={scrollToPayment} />
    </div>
  );
}

// ─── Section dispatcher ──────────────────────────────────────────────────
function SectionRenderer({ section, lang, settings, onScrollPayment }) {
  const d = section.data || {};
  const t = d.template;
  switch (t) {
    case "hero":              return <HeroSection d={d} lang={lang} onScroll={onScrollPayment} />;
    case "editorial_text":    return <EditorialText d={d} lang={lang} />;
    case "full_bleed_image":  return <FullBleedImage d={d} lang={lang} />;
    case "payment":           return <PaymentBlock d={d} lang={lang} settings={settings} />;
    case "stats_showcase":    return <StatsShowcase d={d} lang={lang} />;
    case "values":            return <ValuesSection d={d} lang={lang} />;
    case "next_chapter":      return <NextChapter d={d} lang={lang} />;
    case "volunteer":         return <VolunteerSection d={d} lang={lang} />;
    case "final_cta":         return <FinalCta d={d} lang={lang} settings={settings} onScroll={onScrollPayment} />;
    default:                  return null;
  }
}

// ─── Templates ───────────────────────────────────────────────────────────
function focalStyle(img) {
  const f = img?.focal || { x: 50, y: 50 };
  return { objectPosition: `${f.x}% ${f.y}%` };
}

function HeroSection({ d, lang, onScroll }) {
  const img = d.images?.[0];
  const hasImg = !!(img?.url);
  return (
    <section className="relative min-h-[86vh] flex items-end overflow-hidden bg-ink text-white">
      {hasImg && (
        <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
             className="absolute inset-0 w-full h-full object-cover" style={focalStyle(img)} />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/10 to-ink/80" />
      <div className="relative container-x max-editorial py-24 md:py-32">
        <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>
        <h1 className="font-editorial text-5xl md:text-7xl lg:text-8xl mt-4 tracking-tighter leading-[0.95]">
          {nl2br(pickLang(d.headline, lang))}
        </h1>
        <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-white/85">
          {nl2br(pickLang(d.body, lang))}
        </p>
        <button onClick={onScroll} data-testid="join-hero-cta"
                className="mt-10 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-[0.16em] uppercase transition-colors">
          {pickLang(d.cta_label, lang)}
        </button>
      </div>
    </section>
  );
}

function EditorialText({ d, lang }) {
  return (
    <section className="container-x max-editorial py-24 md:py-32">
      {pickLang(d.eyebrow, lang) && <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>}
      <h2 className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter leading-[1]">
        {nl2br(pickLang(d.headline, lang))}
      </h2>
      <div className="mt-8 max-w-3xl text-lg md:text-xl leading-relaxed text-ink/80">
        <RichText text={pickLang(d.body, lang)} gapClassName="mt-5" />
      </div>
    </section>
  );
}

function FullBleedImage({ d, lang }) {
  const img = d.images?.[0];
  if (!img?.url) {
    return (
      <div className="container-x max-editorial py-16">
        <div className="aspect-[16/9] bg-line/20 border border-dashed border-line flex items-center justify-center text-ink/30 text-sm">
          Image not selected yet
        </div>
        {pickLang(d.caption, lang) && (
          <div className="mt-4 text-ink/60 font-editorial text-2xl whitespace-pre-line">{pickLang(d.caption, lang)}</div>
        )}
      </div>
    );
  }
  return (
    <section className="relative w-full">
      <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden">
        <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
             className="w-full h-full object-cover" style={focalStyle(img)} />
      </div>
      {pickLang(d.caption, lang) && (
        <div className="container-x max-editorial mt-6 mb-6 font-editorial text-2xl md:text-3xl tracking-tight text-ink whitespace-pre-line">
          {pickLang(d.caption, lang)}
        </div>
      )}
    </section>
  );
}

function PaymentBlock({ d, lang, settings }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(settings.identifier || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };
  return (
    <section id="payment" className="container-x max-editorial py-24 md:py-32 scroll-mt-24" data-testid="join-payment">
      <div className="border-2 border-ink bg-white p-8 md:p-14 max-w-3xl mx-auto">
        <div className="label-mono text-terracotta text-center">{pickLang(d.eyebrow, lang) || "BECOME A MEMBER"}</div>
        <div className="mt-4 text-center">
          <div className="font-editorial text-6xl md:text-8xl tracking-tighter tabular-nums" data-testid="join-price">
            {settings.price_dkk} <span className="text-3xl md:text-5xl text-ink/60">{settings.currency}</span>
          </div>
          <div className="mt-2 label-mono text-ink/60 tracking-widest">/ {settings.cadence === "year" ? (lang === "da" ? "ÅR" : lang === "es" ? "AÑO" : "YEAR") : settings.cadence}</div>
        </div>
        <div className="my-10 border-t border-line" />
        <div className="text-center">
          <div className="label-mono text-ink/60 tracking-widest text-[11px]">{settings.method_label || "MobilePay"}</div>
          <div className="mt-4 flex items-center justify-center gap-4 flex-wrap">
            <div className="font-editorial text-5xl md:text-7xl tracking-tighter tabular-nums" data-testid="join-mobilepay-id">
              {settings.identifier}
            </div>
            <button onClick={copy} data-testid="join-copy-btn"
                    className="border border-ink text-ink px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
              {copied ? (lang === "da" ? "Kopieret" : lang === "es" ? "Copiado" : "Copied") : (lang === "da" ? "Kopiér" : lang === "es" ? "Copiar" : "Copy")}
            </button>
          </div>
        </div>
        <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded text-center text-ink" data-testid="join-instruction">
          <div className="text-base md:text-lg font-medium">{pickLang(settings.instruction, lang)}</div>
        </div>
        {pickLang(d.note_after, lang) && (
          <p className="mt-6 text-sm text-ink/60 text-center whitespace-pre-line">{pickLang(d.note_after, lang)}</p>
        )}
      </div>
    </section>
  );
}

function StatsShowcase({ d, lang }) {
  const img = d.images?.[0];
  const stats = d.stats || [];
  return (
    <section className="bg-ink text-white py-24 md:py-32">
      <div className="container-x max-editorial">
        {pickLang(d.eyebrow, lang) && <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>}
        <h2 className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter leading-[1]">
          {nl2br(pickLang(d.headline, lang))}
        </h2>
        {img?.url && (
          <div className="mt-12 aspect-[21/9] overflow-hidden">
            <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
                 className="w-full h-full object-cover" style={focalStyle(img)} />
          </div>
        )}
        <div className="mt-12 grid md:grid-cols-3 gap-8 md:gap-12">
          {stats.map((s, i) => (
            <div key={i} className="border-l-2 border-terracotta pl-6">
              <div className="font-editorial text-4xl md:text-5xl tracking-tighter">{s.value}</div>
              <div className="mt-3 text-white/70 leading-relaxed">{pickLang(s.label, lang)}</div>
            </div>
          ))}
        </div>
        {d.cta_url && pickLang(d.cta_label, lang) && (
          <Link to={d.cta_url} data-testid="join-social-run-link"
                className="mt-12 inline-flex items-center gap-3 text-white hover:text-terracotta underline underline-offset-8 decoration-white/30 hover:decoration-terracotta text-sm tracking-[0.16em] uppercase">
            {pickLang(d.cta_label, lang)} →
          </Link>
        )}
      </div>
    </section>
  );
}

function ValuesSection({ d, lang }) {
  const values = d.values || [];
  return (
    <section className="container-x max-editorial py-24 md:py-32">
      {pickLang(d.eyebrow, lang) && <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>}
      <h2 className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter leading-[1]">
        {nl2br(pickLang(d.headline, lang))}
      </h2>
      <div className="mt-12 space-y-8 md:space-y-10 max-w-3xl">
        {values.map((v, i) => (
          <div key={v.key || i} className="border-t border-line pt-6">
            <div className="label-mono text-terracotta text-[11px] tracking-widest">{pickLang(v.name, lang).toUpperCase()}</div>
            <div className="mt-3 text-lg md:text-xl text-ink/80 leading-relaxed">{pickLang(v.text, lang)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NextChapter({ d, lang }) {
  const img = d.images?.[0];
  return (
    <section className="relative bg-ink text-white overflow-hidden">
      {img?.url && (
        <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
             className="absolute inset-0 w-full h-full object-cover opacity-40" style={focalStyle(img)} />
      )}
      <div className="relative container-x max-editorial py-24 md:py-32">
        <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>
        <h2 className="font-editorial text-4xl md:text-6xl mt-4 tracking-tighter leading-[1]">
          {nl2br(pickLang(d.headline, lang))}
        </h2>
        <div className="mt-6 max-w-2xl text-lg text-white/80 leading-relaxed">
          <RichText text={pickLang(d.body, lang)} gapClassName="mt-5" />
        </div>
        {d.cta_url && pickLang(d.cta_label, lang) && (
          <Link to={d.cta_url} data-testid="join-next-chapter-link"
                className="mt-10 inline-block border border-white text-white px-8 py-4 text-sm tracking-[0.16em] uppercase hover:bg-white hover:text-ink transition-colors">
            {pickLang(d.cta_label, lang)}
          </Link>
        )}
      </div>
    </section>
  );
}

function FinalCta({ d, lang, settings, onScroll }) {
  const img = d.images?.[0];
  return (
    <section className="relative overflow-hidden">
      {img?.url && (
        <div className="absolute inset-0">
          <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
               className="w-full h-full object-cover" style={focalStyle(img)} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>
      )}
      <div className="relative container-x max-editorial py-24 md:py-32 text-white">
        {pickLang(d.eyebrow, lang) && <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>}
        <h2 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[1]">
          {nl2br(pickLang(d.headline, lang))}
        </h2>
        <div className="mt-6 max-w-2xl text-lg md:text-xl text-white/85 leading-relaxed">
          <RichText text={pickLang(d.body, lang)} gapClassName="mt-5" />
        </div>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
          <div>
            <div className="font-editorial text-4xl tracking-tighter">
              {settings.price_dkk} {settings.currency} <span className="text-lg text-white/60">/ {settings.cadence === "year" ? (lang === "da" ? "år" : lang === "es" ? "año" : "year") : settings.cadence}</span>
            </div>
            <div className="mt-1 text-white/70 text-sm">{settings.method_label || "MobilePay"}: <span className="font-editorial text-2xl">{settings.identifier}</span></div>
          </div>
          <button onClick={onScroll} data-testid="join-final-cta"
                  className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-[0.16em] uppercase transition-colors">
            {pickLang(d.cta_label, lang)}
          </button>
        </div>
        {pickLang(d.footer_line, lang) && (
          <div className="mt-16 border-t border-white/20 pt-6 font-editorial text-xl tracking-tight text-white/80">
            {pickLang(d.footer_line, lang)}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Volunteer section (editorial intro + inline form) ──────────────────
// Structural help-with options — the KEYS are stable so the backend always
// receives the same identifiers. Labels are localised at render time.
const VOL_HELP_OPTIONS = [
  { key: "events",           en: "Events",                       da: "Arrangementer",             es: "Eventos" },
  { key: "sport",            en: "Sport",                        da: "Sport",                     es: "Deporte" },
  { key: "music_culture",    en: "Music & culture",              da: "Musik & kultur",            es: "Música y cultura" },
  { key: "food_hospitality", en: "Food & hospitality",           da: "Mad & værtskab",            es: "Comida y hostelería" },
  { key: "photo_video",      en: "Photography / video",          da: "Foto / video",              es: "Fotografía / vídeo" },
  { key: "social_media",     en: "Social media / communication", da: "Sociale medier / kommunikation", es: "Redes sociales / comunicación" },
  { key: "design",           en: "Design",                       da: "Design",                    es: "Diseño" },
  { key: "logistics",        en: "Practical help / logistics",   da: "Praktisk hjælp / logistik", es: "Ayuda práctica / logística" },
  { key: "fundraising",      en: "Fundraising / partnerships",   da: "Fundraising / samarbejder", es: "Recaudación / colaboraciones" },
  { key: "own_idea",         en: "I have another idea",          da: "Jeg har en anden idé",      es: "Tengo otra idea" },
  { key: "not_sure",         en: "I'm not sure yet — just contact me", da: "Jeg er ikke sikker endnu — kontakt mig bare", es: "Aún no estoy seguro/a — contáctame" },
];
const VOL_AVAILABILITY = [
  { key: "specific_event",   en: "Just for a specific event",     da: "Kun til et bestemt arrangement",   es: "Solo para un evento concreto" },
  { key: "occasionally",     en: "Occasionally",                  da: "Ind imellem",                      es: "Ocasionalmente" },
  { key: "more_involved",    en: "I'd like to get more involved", da: "Jeg vil gerne være mere involveret", es: "Me gustaría implicarme más" },
  { key: "not_sure",         en: "Not sure yet",                  da: "Ved det ikke endnu",               es: "Aún no lo sé" },
];

function VolunteerSection({ d, lang }) {
  const img = d.images?.[0];
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [form, setForm] = useState({
    name: "", email: "", phone: "", languages: "",
    help_with: [], availability: "", message: "",
    consent: false,
    website: "",  // honeypot — must stay empty
  });

  const toggleHelp = (k) => setForm(f => ({
    ...f, help_with: f.help_with.includes(k) ? f.help_with.filter(x => x !== k) : [...f.help_with, k]
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot triggered — silently drop
    if (!form.consent) return;
    if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return;
    if (form.help_with.length === 0) return;
    setStatus("sending");
    try {
      // Reuse the existing community/interest endpoint — same email pipeline
      // that already delivers to danamarksolidaritet@gmail.com with reply-to
      // set to the volunteer's own address. No new email system.
      const helpLabels = form.help_with.map(k => (VOL_HELP_OPTIONS.find(o => o.key === k) || {}).en || k);
      const availLabel = (VOL_AVAILABILITY.find(o => o.key === form.availability) || {}).en || form.availability;
      await api.post("/community/interest", {
        kind: "volunteer",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        languages: form.languages.trim(),
        help_with: helpLabels.join(", "),
        availability: availLabel,
        message: form.message.trim(),
        language: lang,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const T = {
    name:      { en: "Name",           da: "Navn",             es: "Nombre" },
    email:     { en: "Email",          da: "E-mail",           es: "Correo" },
    phone:     { en: "Phone",          da: "Telefon",          es: "Teléfono" },
    languages: { en: "Languages",      da: "Sprog",            es: "Idiomas" },
    helpQ:     { en: "How would you like to help?",  da: "Hvordan vil du gerne hjælpe?",   es: "¿Cómo te gustaría ayudar?" },
    tellUs:    { en: "Tell us a little about yourself", da: "Fortæl os lidt om dig selv",  es: "Cuéntanos un poco sobre ti" },
    tellPh:    { en: "Anything you'd like us to know? An idea, a skill, why you'd like to join us… no need to write an application.",
                 da: "Er der noget, vi bør vide? En idé, en færdighed, hvorfor du gerne vil være med… ingen grund til at skrive en ansøgning.",
                 es: "¿Algo que quieras que sepamos? Una idea, una habilidad, por qué te gustaría sumarte… no hace falta que escribas una solicitud." },
    availQ:    { en: "How much time would you normally like to give?", da: "Hvor meget tid vil du normalt gerne give?", es: "¿Cuánto tiempo te gustaría dar habitualmente?" },
    consent:   { en: "I agree that DANAmark Solidaritet may contact me about volunteering.",
                 da: "Jeg accepterer, at DANAmark Solidaritet må kontakte mig om frivilligt arbejde.",
                 es: "Acepto que DANAmark Solidaritet pueda contactarme sobre voluntariado." },
    submit:    { en: "Send to DANAmark", da: "Send til DANAmark", es: "Enviar a DANAmark" },
    required:  { en: "required",       da: "obligatorisk",     es: "obligatorio" },
    optional:  { en: "optional",       da: "valgfri",          es: "opcional" },
    errMsg:    { en: "Something went wrong and your message wasn't sent. Please try again.",
                 da: "Noget gik galt, og din besked blev ikke sendt. Prøv igen.",
                 es: "Algo salió mal y tu mensaje no se envió. Inténtalo de nuevo." },
  };
  const pk = (obj) => obj[lang] || obj.en;

  return (
    <section id="volunteer" className="bg-white scroll-mt-24" data-testid="join-volunteer">
      <div className="container-x max-editorial py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          {img?.url ? (
            <div className="aspect-[4/5] overflow-hidden">
              <img src={resolveImageUrl(img.url)} alt={pickLang(img.alt, lang)}
                   className="w-full h-full object-cover" style={focalStyle(img)} />
            </div>
          ) : (
            <div className="aspect-[4/5] bg-line/10 border border-dashed border-line flex items-center justify-center text-ink/25 text-sm">
              Volunteer photo not selected yet
            </div>
          )}
        </div>
        <div className="md:col-span-6 md:pl-4">
          <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>
          <h2 className="mt-3 font-editorial text-4xl md:text-6xl tracking-tighter leading-[1]">
            {nl2br(pickLang(d.headline, lang))}
          </h2>
          <div className="mt-6 text-lg md:text-xl text-ink/80 leading-relaxed">
            <RichText text={pickLang(d.body, lang)} gapClassName="mt-5" />
          </div>
          {!showForm && status !== "success" && (
            <button onClick={() => setShowForm(true)} data-testid="volunteer-cta"
                    className="mt-10 border-2 border-ink text-ink hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-[0.16em] uppercase transition-colors">
              {pickLang(d.cta_label, lang)}
            </button>
          )}
          {status === "success" && (
            <div className="mt-10 border-t border-line pt-8" data-testid="volunteer-success">
              <div className="label-mono text-terracotta">{pickLang(d.eyebrow, lang)}</div>
              <h3 className="mt-3 font-editorial text-3xl tracking-tighter">{pickLang(d.success_headline, lang)}</h3>
              <p className="mt-4 text-ink/70 leading-relaxed">{pickLang(d.success_body, lang)}</p>
            </div>
          )}
        </div>
      </div>

      {showForm && status !== "success" && (
        <div className="border-t border-line bg-cream/50">
          <form onSubmit={submit} className="container-x max-editorial py-16 md:py-20 max-w-3xl" data-testid="volunteer-form">
            {pickLang(d.form_intro, lang) && (
              <p className="text-sm text-ink/60 mb-8 max-w-xl">{pickLang(d.form_intro, lang)}</p>
            )}

            {/* Honeypot — hidden from real users, bots fill it and get dropped */}
            <input type="text" name="website" tabIndex="-1" autoComplete="off"
                   value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                   className="hidden" aria-hidden="true" />

            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{pk(T.name)} <span className="text-terracotta">*</span></div>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                       data-testid="vol-name" maxLength={200}
                       className="border border-line px-3 py-3 w-full bg-white" />
              </label>
              <label className="block">
                <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{pk(T.email)} <span className="text-terracotta">*</span></div>
                <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                       data-testid="vol-email" maxLength={200}
                       className="border border-line px-3 py-3 w-full bg-white" />
              </label>
              <label className="block">
                <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{pk(T.phone)} <span className="text-ink/40">({pk(T.optional)})</span></div>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                       maxLength={50} className="border border-line px-3 py-3 w-full bg-white" />
              </label>
              <label className="block">
                <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{pk(T.languages)} <span className="text-ink/40">({pk(T.optional)})</span></div>
                <input value={form.languages} onChange={e => setForm(f => ({ ...f, languages: e.target.value }))}
                       maxLength={200} className="border border-line px-3 py-3 w-full bg-white" />
              </label>
            </div>

            <div className="mt-8">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-3">{pk(T.helpQ)} <span className="text-terracotta">*</span></div>
              <div className="grid sm:grid-cols-2 gap-2">
                {VOL_HELP_OPTIONS.map(opt => {
                  const checked = form.help_with.includes(opt.key);
                  return (
                    <label key={opt.key} data-testid={`vol-help-${opt.key}`}
                           className={`flex items-center gap-2 border px-3 py-2 cursor-pointer text-sm ${checked ? "bg-ink text-white border-ink" : "border-line bg-white hover:border-ink"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleHelp(opt.key)}
                             className="w-4 h-4 accent-terracotta" />
                      <span>{opt[lang] || opt.en}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className="block mt-8">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{pk(T.tellUs)} <span className="text-ink/40">({pk(T.optional)})</span></div>
              <textarea rows={5} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                        placeholder={pk(T.tellPh)} maxLength={2000}
                        data-testid="vol-message"
                        className="border border-line px-3 py-3 w-full bg-white text-sm leading-relaxed" />
            </label>

            <div className="mt-6">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-3">{pk(T.availQ)}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {VOL_AVAILABILITY.map(opt => {
                  const active = form.availability === opt.key;
                  return (
                    <button type="button" key={opt.key} onClick={() => setForm(f => ({ ...f, availability: opt.key }))}
                            data-testid={`vol-avail-${opt.key}`}
                            className={`text-left border px-3 py-2 text-sm cursor-pointer ${active ? "bg-ink text-white border-ink" : "border-line bg-white hover:border-ink"}`}>
                      {opt[lang] || opt.en}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="mt-8 flex items-start gap-3 text-sm text-ink/70 cursor-pointer">
              <input type="checkbox" required checked={form.consent}
                     onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                     data-testid="vol-consent"
                     className="mt-1 w-4 h-4 accent-terracotta" />
              <span>{pk(T.consent)}</span>
            </label>

            <div className="mt-10 flex items-center gap-4">
              <button type="submit" disabled={status === "sending"}
                      data-testid="vol-submit"
                      className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-[0.16em] uppercase disabled:opacity-50">
                {status === "sending" ? "…" : pk(T.submit)}
              </button>
              {status === "error" && (
                <span className="text-sm text-rose-700">{pk(T.errMsg)}</span>
              )}
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// ─── Sticky mobile CTA ───────────────────────────────────────────────────
function StickyMobileCta({ settings, lang, onClick }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  const label = lang === "da" ? `Bliv medlem · ${settings.price_dkk} DKK`
              : lang === "es" ? `Hazte miembro · ${settings.price_dkk} DKK`
                              : `Become a member · ${settings.price_dkk} DKK`;
  return (
    <div className="md:hidden fixed bottom-4 left-4 right-4 z-40" data-testid="join-sticky-cta">
      <div className="flex items-center gap-2 bg-ink text-white shadow-2xl">
        <button onClick={onClick} className="flex-1 px-5 py-4 text-sm tracking-[0.14em] uppercase text-left">
          {label}
        </button>
        <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="px-4 py-4 text-white/60 hover:text-white text-lg">×</button>
      </div>
    </div>
  );
}
